import React, {useState, useEffect} from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import {toTime, toTimeType, timeConverter, isObjectEmpty, uploadCSV, replaceAll, timeConverterSM} from '../../../Utils';
import Spinner from '../../common/generic/Spinner';

const ContainerComp = ({pageState, reportState, csvData, setcsvData}) =>{
    const {database, deleteThis, request, fetchModules, modulesData, modulesReady } = pageState;
    const [processedMarks, setProcessedMarks] = useState({});
    const [markRecords, setMarkRecords] = useState({});
    const {selectedStudents, selectedProgram} = reportState;

    /*
    CSV NOTES
    =========
    There is a big problem when trying to upload more then 1 csv file.  The below system will reset all the data in 
    this component and start it over when when the user clicks save.
    
    Steps
    1) User clicks save in either ProgramReportTableComp or AcitivityReportComp
        1b) openCSVPage() will populate csvData using setcsvData({processStage:'newRequest'});
    2) ViewReportsPage checks to see if the marks have changed (* seems to be called way to many times...)
        2b) setReportType(csvData.reportType);
            setSelectedProgram({...csvData.selectedProgram});
            setSelectedStudents([...csvData.selectedStudents]); 
    3) Start the report
        3a) CSV Step 1: checks if a new request for csv and starts to clear the data
        3b) CSV Step 2: clears the data 
        3c) CSV Step 3: keep checking to see when the data has been cleared
        3d) CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
        3e) (The normal Startup starts at CSV Step 1) 
    */

    // CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
    // The normal Startup starts at CSV Step 1 
    const tryLoadingCSVStage2 = () => {
        if(!csvData) return;
        if(csvData.processStage === 'clearingData'){
            if(!database['getProgramMarks'] && isObjectEmpty(markRecords) && isObjectEmpty(processedMarks)){
                setcsvData({...csvData, processStage:'clearedData'});
            }
        }
    };
    // CSV Step 3: keep checking to see when the data has been cleared
    useEffect(() =>{tryLoadingCSVStage2();},[database['getProgramMarks']]);
    useEffect(() =>{tryLoadingCSVStage2();},[markRecords]);
    useEffect(() =>{tryLoadingCSVStage2();},[processedMarks]);

    // CSV Step 2: clears the data
    useEffect(() => {
        if(!csvData) return;

        if(csvData.processStage === 'clearingData'){
            deleteThis('getProgramMarks');
            setMarkRecords({});
            setProcessedMarks({});
        }
    },[csvData]);

    // CSV Step 1: checks if a new request for csv and starts to clear the data 
    // Normal Startup Step 1:
    useEffect(() => {
        if(csvData){
            if(csvData.processStage==='newRequest'){
                // csvData.setloadingCSV({...csvData.loadingCSV, hotspot:true});
                setcsvData({...csvData, processStage:'clearingData'});
                return;
            }

            if(csvData.processStage==='clearingData') return;
        }

        if(!isObjectEmpty(markRecords)) return; // already loading

        if(!database['getProgramMarks']){
            let studentQueries = selectedStudents.map(studentRec=>{
                let getMarksQuery={
                    params: studentRec.id+'~'+selectedProgram.id.toLowerCase(),
                    func:'getProgramMarks'            
                };
                return getMarksQuery;
            });

            if(studentQueries.length > 0){
                let counter = 1;
                studentQueries = studentQueries.map((query,i)=>{
                    let prevQuery = (i!=0) ? studentQueries[i-1] : 'undefinded';
                    if(prevQuery !== 'undefinded'){
                        counter++;
                        query.nextRequest = prevQuery;
                    }
                    return query;
                });
                let loadChainedMarks = studentQueries[studentQueries.length-1];
                loadChainedMarks.numQueries = counter;
                setMarkRecords({info:{numStudents:counter,numLoaded:counter}});

                // pass the link list of saved changes into the database
                request(loadChainedMarks); 
            }
            else
                console.log('Nothing to load?');
        }
    });

    // convert database['getProgramMarks'] into setMarkRecords
    // UDATES - when database['getProgramMarks'] changes
    useEffect(() => {
        if(csvData && csvData.processStage !== 'clearedData') return; // don't setup markRecords for csv files without being ready 
        if(isObjectEmpty(markRecords)) return;
        (!pageState.isFetchingModules && !modulesReady) && fetchModules(selectedProgram.id.toLowerCase());
        if(!database['getProgramMarks']) return;
        
        let marks = {};
        let key = 'unavailable';
        let counter = {...markRecords.info};

        if(database['getProgramMarks'].isLoaded) counter.numLoaded--;
        
        marks = {
            isloaded: database['getProgramMarks'].isLoaded,
            data: database['getProgramMarks'].data
        }; 

        if((marks.data) && !isObjectEmpty(marks.data)){
            key = Object.keys(marks.data)[0].split('+')[0];
        }

        setMarkRecords({...markRecords, info:{...counter}, [key]:marks});

    }, [database['getProgramMarks']]); 

    const tryLoadingNow = () => {
        if(isObjectEmpty(markRecords)) return;
        if(!isObjectEmpty(processedMarks)) return;
        if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return;

        let reportData = JSON.parse(localStorage.getItem('reportData'));
        let masterLevel = parseInt(reportData.masterLevel)/100;

        if(modulesReady) { 
            let studentsModuleMarks = selectedStudents.reduce((acc,studentRec)=>{
                let studentID = studentRec.id;
                let studentMarkRecords = markRecords[studentID];
                if(!studentMarkRecords) return acc; // still loading marks

                let hotSpotCnt = {noneHotSpots:0, hotSpots:0};
                let marksPerModule = modulesData.map(moduleRec => { // foreach module
                    let moduleID = moduleRec.moduleID;
                    return moduleRec.links.map(linkRec => // foreach activity in a module attach marks to it.
                    {                   
                        let linkID= linkRec.linkID;
                        if(studentMarkRecords.data[`${studentID}+${moduleID[0]}+${linkID}`]) {
                            let mark = studentMarkRecords.data[`${studentID}+${moduleID[0]}+${linkID}`];
                            if (parseInt(mark.TimesAttempted) > 2 && parseInt(mark.TimesCompleted) ===0){
                                hotSpotCnt.hotSpots++;
                                return {moduleID, linkID, name: linkRec.name, unitName: linkRec.unit.name, mark: studentMarkRecords.data[`${studentID}+${moduleID[0]}+${linkID}`]};
                            }
                            else{
                                hotSpotCnt.noneHotSpots++;
                            }
                        }
                    }).filter(e=> e); // remove empty records (ones with no marks)
                });

                acc[`${studentRec.id}+${studentRec.name}`] = {moduleMarks:marksPerModule,hotSpots:hotSpotCnt};
                return acc;
            },{});
            setProcessedMarks(studentsModuleMarks);

            if(csvData){
                let newLine = '%0A';
                let csvString = '';
                csvString =`"An activity which the student has attempted three times without mastery (${masterLevel}%) is a 'hotspot'"${newLine}`;
                csvString +=`"Students without marks will not appear in this report"${newLine}`;

                let tableHeaderStr = '"Unit Name","Activity Name","Number of Attempts","Mastered*","Highest Mark","Average Mark","Last Mark","Improvement",Time Spent","Date"'+newLine;

                Object.keys(studentsModuleMarks).forEach((key) => { 
                    let headerStr = `${newLine}"${key.split('+')[1]}"${newLine}"${selectedProgram.name}"${newLine}"Activity Report"${newLine}`;
                    let tableData  = studentsModuleMarks[key].moduleMarks;
                    let hotSpotData = studentsModuleMarks[key].hotSpots;

                    csvString += headerStr + tableHeaderStr;
                    tableData.forEach((moduleRec) => {
                        let moduleTitleStr = (moduleRec.length > 0) ? `"${moduleRec[0].moduleID[1]}"${newLine}` : '';
                        csvString += moduleTitleStr;
                        moduleRec.forEach((markRec, mark_i) => {
                            const { unitName, name, mark} = markRec;
                            const { TimesAttempted, TimesCompleted, HighestMark, LastMark, QuestionsRight, QuestionsAttempted, CumulativeTime, LastMastered} = mark;
                            let recordStr = `"${unitName}","${name}","${TimesAttempted}","${TimesCompleted}","${HighestMark}%","${LastMark}%","${Math.round((QuestionsRight/QuestionsAttempted)*100)}%","${'-'}","${toTime(CumulativeTime)} ${toTimeType(CumulativeTime)}","${timeConverter(parseInt(LastMastered))}"${newLine}`;
                            csvString += recordStr;
                        });
                    });
                    csvString +=`"Of the ${hotSpotData.noneHotSpots} different activities that this student has worked on, ${hotSpotData.hotSpots} are hotspots."${newLine}`;
                });
                
                let name = '';
                if(Object.keys(studentsModuleMarks).length===0){
                    window.alert('There are no marks for the student(s) selected');
                    csvData.setloadingCSV({...csvData.loadingCSV, hotspot:false});
                    return;
                }

                if(Object.keys(studentsModuleMarks).length==1)
                    name = Object.keys(studentsModuleMarks)[0].split('+')[1]+'_';

                let today = new Date();
                today = timeConverterSM(today.getTime());

                uploadCSV(name+'HotSpotReport_'+selectedProgram.name+'_'+today,csvString);
                //uploadCSV('hotspotReport',csvString);
                csvData.setloadingCSV({...csvData.loadingCSV, hotspot:false});
            }
        };
    }; 
    useEffect(() =>{tryLoadingNow();},[modulesData]);
    useEffect(() =>{tryLoadingNow();},[markRecords]);

    if(csvData) return <></>;
    if(isObjectEmpty(markRecords)) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;
    if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;

    return <>
        {Object.keys(processedMarks).length < selectedStudents.length && <center> <p>  Students without marks will not appear in this report </p></center>}
        {Object.keys(processedMarks).map((key, i) => (
            <span key={i}><br/> 
            <HotSpotReportComp header={{student: key.split('+')[1], program: selectedProgram.name}} tableData={processedMarks[key].moduleMarks} hotSpotData={processedMarks[key].hotSpots}/>
            <br/></span>
        ))}
    </>;
};

const HotSpotReportComp = ({header, tableData, hotSpotData}) => {
    const [masterLevel, setMasterLevel] = useState(0.8);

    useEffect(()=>{
        let reportData = JSON.parse(localStorage.getItem('reportData'));
        if(masterLevel !== (parseInt(reportData.masterLevel)/100))
            setMasterLevel(parseInt(reportData.masterLevel)/100);
    });

    const titleRow = <Row style={{ fontSize:'0.9em', fontWeight:'bold'}}>
    <Col xs='5'><Row>
        <Col style={{textAlign: 'left'}} xs='7'> Unit Name </Col>
        <Col style={{textAlign: 'left'}} xs='5'> Activity Name </Col>
        </Row>
    </Col>
    <Col xs='7'><Row>
        <Col style={{textAlign: 'center'}} xs='1'>Number of Attempts</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Mastered*</Col>
        <Col style={{textAlign: 'center'}} xs='1'>Highest Mark</Col>
        <Col style={{textAlign: 'center'}} xs='1'>Average Mark</Col>
        <Col style={{textAlign: 'center'}} xs='1'>Last Mark</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Improvement</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Time Spent</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Date</Col>
        </Row></Col>
    </Row>;

    const headerComp = <center>
        <h4><Row>
            <Col xs='4'>{header.student}</Col> 
            <Col xs='4'>{header.program} </Col>
            <Col xs='4'>Hot Spot Report</Col>
        </Row></h4>
    </center>;

    // eslint-disable-next-line react/no-unescaped-entities
    const Footer = () => <center><hr/>An activity which the student has attempted three times without mastery ({masterLevel*100}%) is a 'hotspot'. <br/> Of the {hotSpotData.noneHotSpots} different activities that this student has worked on, {hotSpotData.hotSpots} are hotspots.</center>;
    return (
        <>
            {headerComp}
            {(hotSpotData.hotSpots!==0) ? <>
            <hr/>
            {titleRow}
            {tableData.map((moduleRec, module_i) => <span key={module_i}>
                {(moduleRec.length > 0) && <Row><Col xs='12' style={{fontWeight:'bold'}} >{moduleRec[0].moduleID[0]}</Col></Row>}
                {moduleRec.map((markRec, mark_i) => 
                    <TableRow key={mark_i} data={markRec} fontWeight={'normal'}/>
                )}
                </span>
            )}
            <Footer/>
            </>:
                <center>There are no hot spots for this student</center>
            }

        </>
    );
};
HotSpotReportComp.propTypes = {
    tableData: PropTypes.array
};

const TableRow = (props) => {
    const { unitName, name, mark} = props.data;
    const { TimesAttempted, TimesCompleted, HighestMark, LastMark, QuestionsRight, QuestionsAttempted, CumulativeTime, LastUsed} = mark;

    return (
        <Row style={{fontSize:'0.9em'}}>
            <Col xs='5'><Row>
                <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='7'> {unitName} </Col>
                <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='5'>{name}</Col>
                </Row>
            </Col>
            <Col xs='7'><Row>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='1'>{TimesAttempted}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{TimesCompleted}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='1'>{HighestMark}%</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='1'>{LastMark}%</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='1'>{Math.round((QuestionsRight/QuestionsAttempted)*100)}%</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{'-'}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{toTime(CumulativeTime)} {toTimeType(CumulativeTime)}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{timeConverter(parseInt(LastUsed))}</Col>
            </Row></Col>
        </Row>
    );
};

TableRow.propTypes = {
    fontWeight: PropTypes.string,
    data: PropTypes.object
};

export default ContainerComp;